/**
 * Needs verification, inside Manifest Review (Dominique Genua, 2026-10-02).
 *
 * A lead routed here ("Needs verification" in the disposition list) is a
 * resident whose benefits a VA must check before anyone works them. The VA
 * answers with one of two words, and a note saying what was found:
 *
 *   Qualifies          back to the PA who held it, or to Leads to place
 *                      (tagged "Verified — qualifies"), or nothing reopens
 *                      when the resident already has open work.
 *   Does not qualify   closes as DNQ; the 6-month re-check applies.
 *
 * The row carries what a VA needs without opening anything: the name, the
 * date of birth, the Medicaid number, the CounselEAR id, what was asked, who
 * asked, and who held the case. Restraint, on purpose: one accent for the
 * act, a word for every state.
 *
 * As a board (2026-10-07): one table row per request, on the same kit as the
 * other Manifest Review tabs. The row opens DOCKED beside the table, where the
 * VA answers; the table carries only short cells.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { refusalWords } from './resubmittalsApi';
import { verificationApi, type VerificationAnswer, type VerificationList, type VerificationRequest } from './verificationApi';
import MfBoard, { useOpenParam, useClientSort, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel } from './board/residents';
import { ToneChip, WordChip } from './board/chips';

const ZONE = 'America/New_York';
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ZONE }) : '—');
/** A YYYY-MM-DD date names a day, not an instant: read it as that day, never shifted by a zone. */
const day = (ymd: string) => new Date(`${ymd.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

type Answer = 'qualifies' | 'does-not-qualify';

/** What the VA reads after answering, in the resident's own name. */
function answerWords(label: string, a: VerificationAnswer, holder: string | null): string {
  if (a.replayed) return 'Already recorded.';
  if (a.state === 'NOT_QUALIFIED') return `${label}: closed as Does not qualify. The 6-month re-check applies.`;
  if (a.reentry === 'PRIOR_HOLDER') return `${label} qualifies: back with ${holder || 'their Patient Advocate'}, placed today.`;
  if (a.reentry === 'LEADS_TO_PLACE') return `${label} qualifies: now in Leads to place, tagged Verified — qualifies.`;
  if (a.reentry === 'ALREADY_OPEN') return `${label} qualifies. They already had open work, so nothing was reopened.`;
  return `${label} qualifies.`;
}

function ask(code: string | undefined, r: { code?: string; message?: string; error?: string; status?: number }): string {
  if (code === 'ALREADY_RESOLVED') return 'Another VA already answered this one. Refresh to see what they decided.';
  return refusalWords(r, 'eMerge did not record this.');
}

type VfRow = VerificationRequest & { building: string };

const NOT_ON_FILE = <span className="board-na">not on file</span>;
const UNASSIGNED_KEY = 'UNASSIGNED';
const HELD_KEY = 'HELD';

const PaCell = ({ r }: { r: VfRow }) => (r.prior_holder_name
  ? <span className="board-clip" title={r.prior_holder_name}>{r.prior_holder_name}</span>
  : <ToneChip tone="quiet">Unassigned lead</ToneChip>);

/** The two answers, in the docked record: each with its required note. */
function AnswerForm({ row, onDone }: { row: VfRow; onDone: (msg: string) => void | Promise<void> }) {
  const [mode, setMode] = useState<Answer | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const label = residentLabel(row);
  const errId = `vf-note-missing-${row.id}`;
  const missing = tried && !note.trim();

  const reset = () => { setMode(null); setNote(''); setTried(false); setErr(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mode) return;
    if (!note.trim()) { setTried(true); return; }
    setBusy(true); setErr(null);
    try {
      const r = mode === 'qualifies' ? await verificationApi.qualifies(row.id, note.trim()) : await verificationApi.doesNotQualify(row.id, note.trim());
      if (!r.success || !r.data) { setErr(ask(r.code, r)); return; }
      await onDone(answerWords(label, r.data, row.prior_holder_name));
    } catch { setErr('Could not reach eMerge. Nothing was changed.'); } finally { setBusy(false); }
  };

  return (
    <div className="mf-panel">
      <p>Check the resident&apos;s benefits, then answer. Qualifies sends the lead back to its PA, or to Leads to place; Does not qualify closes it.</p>
      {!mode && (
        <div className="mf-acts">
          <button type="button" className="board-btn board-btn--primary" onClick={() => setMode('qualifies')}>Qualifies</button>
          <button type="button" className="board-btn" onClick={() => setMode('does-not-qualify')}>Does not qualify</button>
        </div>
      )}
      {mode && (
        <form className="rs-cancel" onSubmit={submit} aria-label={`${mode === 'qualifies' ? 'Qualifies' : 'Does not qualify'}: ${label}`}>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} disabled={busy}
            placeholder={mode === 'qualifies' ? 'What you found (required)' : 'Why they do not qualify (required)'}
            aria-label={`Note for ${label}`} aria-required aria-invalid={missing ? true : undefined} aria-describedby={missing ? errId : undefined} />
          <button type="submit" className="mf-go" disabled={busy}>{busy ? 'Recording…' : mode === 'qualifies' ? 'Record: qualifies' : 'Record: does not qualify'}</button>
          <button type="button" className="rs-quiet" onClick={reset} disabled={busy}>Cancel</button>
          {missing && <p id={errId} className="rs-alert" role="alert">Say what you found. The note stays on the record.</p>}
          {err && <p className="rs-alert" role="alert">{err}</p>}
        </form>
      )}
    </div>
  );
}

const SORTERS: Record<string, (r: VfRow) => string | number | null> = {
  asked: (r) => (r.requested_at ? new Date(r.requested_at).getTime() : null),
};

export default function VerificationDesk() {
  const [data, setData] = useState<VerificationList | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await verificationApi.list();
      if (r.success && r.data) setData(r.data);
      else setProblem(refusalWords(r, 'Could not load the verification list.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // One row per request, in the server's order: its building's group, then its items.
  const all: VfRow[] = useMemo(() => (data ? data.groups.flatMap((g) => g.items.map((r) => ({ ...r, building: g.facility_name || 'Building not recorded' }))) : []), [data]);
  const canAct = Boolean(data?.can_act);
  // The VA's find (name, CounselEAR id, Medicaid number or building) narrows what is drawn; the count stays the list's, and the board says how many are shown.
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const found = q ? all.filter((r) => `${residentLabel(r)} ${r.counselear_id || ''} ${r.medicaid_number || ''} ${r.building}`.toLowerCase().includes(q)) : all;
    return legendKey ? found.filter((r) => (r.prior_holder_name ? HELD_KEY : UNASSIGNED_KEY) === legendKey) : found;
  }, [all, legendKey, filter]);
  const sort = useClientSort<VfRow>(shown, SORTERS);

  const answered = async (m: string) => { setNotice(m); setOpen(null); await load(); };

  const heldSample = all.find((r) => r.prior_holder_name);
  const legendItems: MfLegendItem[] = [
    { key: HELD_KEY, label: 'PA holds it', sample: heldSample ? <PaCell r={heldSample} /> : <span className="board-clip">a Patient Advocate</span>, meaning: 'A Patient Advocate held the case; Qualifies places it back with them.', count: all.filter((r) => r.prior_holder_name).length },
    { key: UNASSIGNED_KEY, label: 'Unassigned lead', sample: <ToneChip tone="quiet">Unassigned lead</ToneChip>, meaning: 'Nobody held the case; Qualifies sends it to Leads to place.', count: all.filter((r) => !r.prior_holder_name).length },
  ];

  const columns: BoardColumn<VfRow>[] = [
    { key: 'building', label: 'Building', help: 'The building the resident lives in. The list is grouped by building, in the server\'s order, until you sort.', render: (r) => <span className="board-clip" title={r.building}>{r.building}</span> },
    { key: 'dob', label: 'Date of birth', help: 'The resident\'s date of birth, as the day it names.', render: (r) => (r.dob ? <span>{day(r.dob)}</span> : NOT_ON_FILE) },
    { key: 'medicaid', label: 'Medicaid', help: 'The resident\'s Medicaid number, for the benefits check.', render: (r) => (r.medicaid_number ? <span>{r.medicaid_number}</span> : NOT_ON_FILE) },
    { key: 'ce', label: 'CounselEAR id', help: 'The resident\'s id in CounselEAR.', render: (r) => (r.counselear_id ? <span>{r.counselear_id}</span> : NOT_ON_FILE) },
    { key: 'by', label: 'Asked by', help: 'Who asked for the check.', render: (r) => (r.requested_by_name ? <span className="board-clip" title={r.requested_by_name}>{r.requested_by_name}</span> : <span className="board-na">not recorded</span>) },
    { key: 'asked', label: 'Asked on', sort: 'asked', help: 'The day the check was asked for.', render: (r) => <span>{when(r.requested_at)}</span> },
    { key: 'pa', label: 'PA', help: 'The Patient Advocate who held the case, or "Unassigned lead" when nobody did.', render: (r) => <PaCell r={r} /> },
    { key: 'status', label: 'Status', help: 'Every row here is waiting for a VA\'s benefits check.', render: () => <WordChip word="NEEDS VERIFICATION" /> },
  ];

  const identity = {
    label: 'Resident',
    help: 'The resident whose benefits a VA must check. Open the row to answer: Qualifies or Does not qualify, each with a note.',
    render: (r: VfRow) => <ResidentName p={r} />,
    name: (r: VfRow) => residentLabel(r),
  };

  const sections = (r: VfRow): BoardDetailSection[] => {
    const out: BoardDetailSection[] = [];
    if (canAct) out.push({ id: 'answer', label: 'Answer', render: () => <AnswerForm row={r} onDone={answered} /> });
    out.push({
      id: 'details', label: 'Details',
      render: () => (
        <div className="mf-panel">
          <dl className="mf-facts">
            <dt>What was asked</dt><dd>{r.note || <span className="board-na">no note</span>}</dd>
            <dt>Asked by</dt><dd>{r.requested_by_name || <span className="board-na">not recorded</span>}</dd>
            <dt>Building</dt><dd>{r.building}</dd>
          </dl>
        </div>
      ),
    });
    return out;
  };

  let state: React.ReactNode = null;
  if (problem) state = <MfLoadFailure message={problem} retry={load} />;
  else if (!data) state = <MfSkeleton bare />;
  else if (all.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="Nobody is waiting for a benefits check." />;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<VfRow>
        title="Needs verification"
        count={data ? all.length : null}
        units={['resident', 'residents']}
        what="Leads sent here so a VA can check benefits. Qualifies sends the lead back to its PA, or to Leads to place; Does not qualify closes it."
        notice={notice ? <p className="mf-notice" role="status">{notice}</p> : null}
        controls={data && all.length > 0 ? (
          <input type="search" aria-label="Find a resident, CounselEAR id or building" placeholder="Find a resident, CounselEAR id or building" value={filter} onChange={(e) => setFilter(e.target.value)} />
        ) : null}
        legend={data && all.length > 0 ? (
          <MfLegend title="PA key" items={legendItems} whole={all.length} units={['resident', 'residents']} exclusive value={legendKey} onSelect={setLegendKey}
            scope="Each resident is either held by a Patient Advocate or an unassigned lead. Press an entry to show only those residents." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={sort.rows}
        rowKey={(r) => r.id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${residentLabel(r)}: the verification`}
        sort={sort.sort}
        dir={sort.dir}
        sorts={sort.sorts}
        onSort={sort.onSort}
        foot={data && !data.can_act ? <p>You can see this list; answering needs the lead-placement permission.</p> : null}
        notes={['"Name not recorded": eMerge holds no name for this resident yet; CounselEAR sends the name after the resident.']}
      />
    </main>
  );
}
